import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { withTheme } from 'emotion-theming';
import createEmotionStyled from 'create-emotion-styled';
import createEmotion from 'create-emotion';
import 'moment';

var defineProperty = function defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var taggedTemplateLiteral = function taggedTemplateLiteral(strings, raw) {
  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
};

var cardTypes = ['tip', 'warning', 'info', 'note', 'striped'];
var borderTypes = ['branded', 'default', 'unbranded'];

var borderType = function borderType(bordered) {
  return borderTypes.includes(bordered) ? '-' + bordered : '';
};

var Card = function Card(_ref) {
  var _classnames;

  var title = _ref.title,
      type = _ref.type,
      bordered = _ref.bordered,
      className = _ref.className,
      children = _ref.children;
  return React.createElement('div', {
    className: classnames('c-o-card', (_classnames = {}, defineProperty(_classnames, 'c-o-card--' + type, type), defineProperty(_classnames, 'c-o-card--bordered' + borderType(bordered), bordered), _classnames), className)
  }, title && React.createElement('h4', null, title), children);
};

Card.propTypes = {
  bordered: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(cardTypes)
};
Card.defaultProps = {
  bordered: false
};
var context = typeof global !== 'undefined' ? global : {};

var _createEmotion = createEmotion(context, {
  nonce: btoa('web-ui-components')
}),
    caches = _createEmotion.caches,
    css = _createEmotion.css,
    cx = _createEmotion.cx,
    flush = _createEmotion.flush,
    getRegisteredStyles = _createEmotion.getRegisteredStyles,
    hydrate = _createEmotion.hydrate,
    injectGlobal = _createEmotion.injectGlobal,
    keyframes = _createEmotion.keyframes,
    merge = _createEmotion.merge,
    sheet = _createEmotion.sheet;

var emotion = /*#__PURE__*/Object.freeze({
  __proto__: null,
  caches: caches,
  css: css,
  cx: cx,
  flush: flush,
  getRegisteredStyles: getRegisteredStyles,
  hydrate: hydrate,
  injectGlobal: injectGlobal,
  keyframes: keyframes,
  merge: merge,
  sheet: sheet
});
var styled = createEmotionStyled(emotion, React);
var theme = {
  baseFontSize: 16
}; //DEBTxCYBG pull this from theme component or global?

var baseSpacing = 8;

var spacing = function spacing(value) {
  var fontMultiple = value * baseSpacing;
  var rems = fontMultiple / theme.baseFontSize;
  return rems + "rem";
};

var breakpoints = {
  xs1: '0',
  xs2: '480px',
  sm1: '540px',
  sm2: '640px',
  md1: '768px',
  md2: '960px',
  lg1: '1024px',
  lg2: '1280px',
  lg3: '1328px',
  xl1: '1420px',
  xl2: '1920px'
};
/* Font size config: usage example ${fontSize('arran')} */

var fontConfig = {
  lewis: {
    xs1: {
      fontSize: 37,
      lineHeight: 1.189
    },
    sm1: {
      fontSize: 37,
      lineHeight: 1.189
    },
    md1: {
      fontSize: 45,
      lineHeight: 1.067
    },
    lg1: {
      fontSize: 52,
      lineHeight: 1.154
    },
    xl1: {
      fontSize: 52,
      lineHeight: 1.154
    }
  },
  skye: {
    xs1: {
      fontSize: 32,
      lineHeight: 1.125
    },
    sm1: {
      fontSize: 32,
      lineHeight: 1.125
    },
    md1: {
      fontSize: 40,
      lineHeight: 1.1
    },
    lg1: {
      fontSize: 45,
      lineHeight: 1.156
    },
    xl1: {
      fontSize: 45,
      lineHeight: 1.156
    }
  },
  shetland: {
    xs1: {
      fontSize: 29,
      lineHeight: 1.241
    },
    sm1: {
      fontSize: 29,
      lineHeight: 1.241
    },
    md1: {
      fontSize: 34,
      lineHeight: 1.176
    },
    lg1: {
      fontSize: 39,
      lineHeight: 1.128
    },
    xl1: {
      fontSize: 39,
      lineHeight: 1.128
    }
  },
  mull: {
    xs1: {
      fontSize: 26,
      lineHeight: 1.231
    },
    sm1: {
      fontSize: 26,
      lineHeight: 1.231
    },
    md1: {
      fontSize: 29,
      lineHeight: 1.241
    },
    lg1: {
      fontSize: 33,
      lineHeight: 1.212
    },
    xl1: {
      fontSize: 33,
      lineHeight: 1.212
    }
  },
  islay: {
    xs1: {
      fontSize: 23,
      lineHeight: 1.217
    },
    sm1: {
      fontSize: 23,
      lineHeight: 1.217
    },
    md1: {
      fontSize: 25,
      lineHeight: 1.28
    },
    lg1: {
      fontSize: 28,
      lineHeight: 1.286
    },
    xl1: {
      fontSize: 28,
      lineHeight: 1.286
    }
  },
  orkney: {
    xs1: {
      fontSize: 20,
      lineHeight: 1.4
    },
    sm1: {
      fontSize: 20,
      lineHeight: 1.4
    },
    md1: {
      fontSize: 22,
      lineHeight: 1.273
    },
    lg1: {
      fontSize: 25,
      lineHeight: 1.28
    },
    xl1: {
      fontSize: 25,
      lineHeight: 1.28
    }
  },
  arran: {
    xs1: {
      fontSize: 18,
      lineHeight: 1.333
    },
    sm1: {
      fontSize: 18,
      lineHeight: 1.333
    },
    md1: {
      fontSize: 19,
      lineHeight: 1.474
    },
    lg1: {
      fontSize: 21,
      lineHeight: 1.333
    },
    xl1: {
      fontSize: 21,
      lineHeight: 1.333
    }
  },
  jura: {
    xs1: {
      fontSize: 16,
      lineHeight: 1.5
    },
    sm1: {
      fontSize: 16,
      lineHeight: 1.5
    },
    md1: {
      fontSize: 16,
      lineHeight: 1.5
    },
    lg1: {
      fontSize: 18,
      lineHeight: 1.556
    },
    xl1: {
      fontSize: 18,
      lineHeight: 1.556
    }
  },
  uist: {
    xs1: {
      fontSize: 14,
      lineHeight: 1.429
    },
    sm1: {
      fontSize: 14,
      lineHeight: 1.429
    },
    md1: {
      fontSize: 14,
      lineHeight: 1.429
    },
    lg1: {
      fontSize: 16,
      lineHeight: 1.5
    },
    xl1: {
      fontSize: 16,
      lineHeight: 1.5
    }
  },
  bute: {
    xs1: {
      fontSize: 12,
      lineHeight: 1.333
    },
    sm1: {
      fontSize: 12,
      lineHeight: 1.333
    },
    md1: {
      fontSize: 13,
      lineHeight: 1.538
    },
    lg1: {
      fontSize: 13,
      lineHeight: 1.538
    },
    xl1: {
      fontSize: 13,
      lineHeight: 1.538
    }
  },
  cumbrae: {
    xs1: {
      fontSize: 11,
      lineHeight: 1.455
    },
    sm1: {
      fontSize: 11,
      lineHeight: 1.455
    },
    md1: {
      fontSize: 12,
      lineHeight: 1.333
    },
    lg1: {
      fontSize: 12,
      lineHeight: 1.333
    },
    xl1: {
      fontSize: 12,
      lineHeight: 1.333
    }
  }
};

var generateSASS = function generateSASS(mqBreakpoint, size, lineHeight) {
  var styles = '';

  if (parseInt(mqBreakpoint) === 0) {
    styles = 'font-size: ' + size + 'px; line-height: ' + lineHeight + ';';
  } else {
    styles = '\n      @media (min-width: ' + mqBreakpoint + ') {\n        font-size: ' + size + 'px;\n        line-height: ' + lineHeight + ';\n      } \n    ';
  }

  return styles;
};

var fontSizes = {};

for (var _fontSize in fontConfig) {
  if (fontConfig.hasOwnProperty(_fontSize)) {
    var style = '';

    for (var breakpoint in fontConfig[_fontSize]) {
      if (fontConfig[_fontSize].hasOwnProperty(breakpoint)) {
        var _fontConfig$_fontSize = fontConfig[_fontSize][breakpoint],
            size = _fontConfig$_fontSize.fontSize,
            lineHeight = _fontConfig$_fontSize.lineHeight;
        style += generateSASS(breakpoints[breakpoint], size, lineHeight);
      }
    }

    fontSizes[_fontSize] = style;
  }
}

var fontSize = function fontSize(font) {
  return fontSizes[font];
};

var integerToHue = function integerToHue(integer) {
  if (typeof integer !== 'number') {
    return new Error(integer + ' was not a number.');
  }

  var hue = integer % 360;
  return hue;
};

var generalPalette = {
  black: 'hsl(0, 0%, 0%)',
  blue: 'hsl(204, 52%, 48%)',
  blueLight: 'hsl(208, 59%, 60%)',
  green: 'hsl(135, 41%, 48%)',
  greenFaint: 'hsl(135, 41%, 70%)',
  greyDarkest: 'hsl(0, 0%, 28%)',
  greyDarker: 'hsl(0, 0%, 42%)',
  grey: 'hsl(0, 0%, 60%)',
  greyLighter: 'hsl(0, 0%, 75%)',
  greyLightest: 'hsl(0, 0%, 85%)',
  orange: 'hsl(29, 77%, 56%)',
  red: 'hsl(8, 52%, 50%)',
  purple: 'hsl(282, 25%, 57%)',
  white: 'hsl(0, 0%, 100%)'
};
var black = generalPalette.black,
    blue = generalPalette.blue,
    blueLight = generalPalette.blueLight,
    green = generalPalette.green,
    greenFaint = generalPalette.greenFaint,
    greyDarker = generalPalette.greyDarker,
    greyDarkest = generalPalette.greyDarkest,
    greyLightest = generalPalette.greyLightest,
    orange = generalPalette.orange,
    red = generalPalette.red,
    grey = generalPalette.grey,
    greyLighter = generalPalette.greyLighter,
    purple = generalPalette.purple,
    white = generalPalette.white;
var colours = {
  backgrounds: {
    alternateBody: 'hsl(212, 100%, 97%)',
    alternateHeader: greyLightest,
    component: white,
    modalBackdrop: black,
    modalBackdropOpaque: 'rgba(0, 0, 0, 0.8)',
    shell: 'hsl(0, 0%, 95.3%)',
    tableCell: 'hsl(0, 0%, 97%)',
    tableCellAlternate: 'hsl(0, 71%, 97%)',
    tableCellHeader: 'hsl(0, 0%, 94%)'
  },
  component: {
    border: greyLightest,
    hover: greyLightest
  },
  disabled: {
    background: greyLightest,
    border: greyDarkest
  },
  neutral: {
    black: black,
    grey: grey,
    greyDarker: greyDarker,
    greyDarkest: greyDarkest,
    greyLighter: greyLighter,
    greyLightest: greyLightest,
    white: white
  },
  telephony: {
    answerCallGradient: 'linear-gradient(hsl(54, 100%, 73%), hsl(49, 99%, 64%));',
    calledGradient: 'linear-gradient(hsl(159, 52%, 67%), hsl(153, 41%, 49%));',
    conferenceCallGradient: 'linear-gradient(hsl(193, 100%, 91%), hsl(196, 96%, 80%));',
    conferenceMaskGradient: 'linear-gradient(hsl(248, 86%, 83%), hsl(260, 94%, 72%));',
    dialingCallGradient: 'linear-gradient(hsl(54, 100%, 73%), hsl(49, 99%, 64%));',
    holdCallGradient: 'linear-gradient(hsl(45, 95%, 75%), hsl(37, 91%, 65%));',
    makeCallGradient: 'linear-gradient(hsl(33, 15%, 86%), hsl(180, 3%, 69%));',
    onCallGradient: 'linear-gradient(hsl(129, 80%, 82%), hsl(127, 67%, 63%));',
    answerCall: 'hsl(89, 91%, 40%)',
    closeCall: 'hsl(353, 98%, 41%)',
    dialCall: 'hsl(89, 91%, 40%)',
    endCall: 'hsl(353, 98%, 41%)',
    holdCall: 'hsl(37, 94%, 49%)',
    resumeCall: 'hsl(89, 91%, 40%)'
  },
  typography: {
    body: greyDarkest,
    supplementaryBody: grey
  },
  utility: {
    active: green,
    archived: greyLightest,
    blocker: 'hsl(0, 60%, 96%)',
    deprecated: 'hsl(45, 100%, 51%)',
    empty: 'hsl(0, 0%, 100%)',
    emptyBorder: 'hsl(0, 0%, 40%)',
    error: red,
    errorBodyBackground: 'hsl(8,52%,96%)',
    focus: blueLight,
    help: 'hsl(210, 38%, 91%)',
    inactive: red,
    indicative: orange,
    information: blue,
    notStarted: greyDarker,
    off: grey,
    offDisabled: greyLighter,
    ongoing: purple,
    paused: blue,
    pending: orange,
    success: green,
    successDisabled: greenFaint,
    warning: orange
  },
  utilityLegacy: {
    infoBackground: 'hsl(210, 46%, 91%)',
    infoBorder: 'hsl(204, 52%, 48%)',
    noteBackground: 'hsl(45, 53%, 94%)',
    noteBorder: 'hsl(46, 88%, 24%)',
    tipBackground: 'hsl(125, 21%, 88%)',
    tipBorder: 'hsl(135, 41%, 48%)',
    warningBackground: 'hsl(0, 60%, 96%)',
    warningBorder: 'hsl(8, 52%, 50%)'
  }
};

var getThemeIcon = function getThemeIcon(vmIcon, generalIcon) {
  return function (themeName) {
    return themeName === 'vm' ? vmIcon : generalIcon;
  };
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}

var GenericIcon = function GenericIcon(_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties(_ref, ["styles"]);

  return React.createElement("svg", _extends({
    width: "96",
    height: "96",
    viewBox: "0 0 96 96",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), React.createElement("path", {
    d: "M62.862 48L22.508 8.587a4.972 4.972 0 0 1 0-7.114l.148-.144a5 5 0 0 1 6.988 0l43.848 42.826A4.96 4.96 0 0 1 74.982 48a4.959 4.959 0 0 1-1.49 3.845L29.644 94.671a5 5 0 0 1-6.988 0l-.148-.144a4.972 4.972 0 0 1 0-7.114L62.862 48z"
  }));
};

var _extends$1 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _objectWithoutProperties$1(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}

var VMIcon = function VMIcon(_ref) {
  var _ref$styles = _ref.styles,
      props = _objectWithoutProperties$1(_ref, ["styles"]);

  return React.createElement("svg", _extends$1({
    height: "96",
    viewBox: "0 0 96 96",
    width: "96",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), React.createElement("path", {
    d: "M75.94 48.24a7.134 7.134 0 0 0-2.098-5.059L30.902.241l-10.12 10.12 37.88 37.88-37.88 37.88 10.12 10.12 42.94-42.94a7.134 7.134 0 0 0 2.097-5.06z"
  }));
};

var getChevronRightSolidLarge = getThemeIcon(VMIcon, GenericIcon);

var hashString = function hashString(str) {
  if (typeof str !== 'string') {
    return new Error(str + ' was not a string.');
  }

  var hash = 0;

  for (var i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash); // eslint-disable-line no-bitwise
  }

  return hash;
};

var _templateObject = taggedTemplateLiteral(['\n\t', '\n\tfont-family: ', ';\n\t', ';\n\t', ';\n'], ['\n\t', '\n\tfont-family: ', ';\n\t', ';\n\t', ';\n']);

var getFontProperties = function getFontProperties(props) {
  var fontProperties = 'font-size: 20px; line-height: 1.3;'; // TODOxCYBG To be revisited with responsive VM typography.
  // if (props.theme.name !== 'vm') {

  fontProperties = props.theme.isCompact ? fontSize('jura') : fontSize('arran'); // }

  return fontProperties;
};

var StyledProminentLabel = styled('div')(_templateObject, getFontProperties, function (props) {
  return props.theme.fonts.display.bold;
}, function (props) {
  return !props.isFlush && 'margin-bottom: ' + spacing(2);
}, function (props) {
  return !props.isFlush && 'margin-right: ' + spacing(2);
});

var ProminentLabel = function ProminentLabel(_ref) {
  var isFlush = _ref.isFlush,
      children = _ref.children;
  return React.createElement(StyledProminentLabel, {
    isFlush: isFlush
  }, children);
};

ProminentLabel.propTypes = {
  /** Allows for content to be text or node (for example IconTextDynamic). */
  children: PropTypes.node.isRequired,

  /** Removed margins. */
  isFlush: PropTypes.bool
};
ProminentLabel.defaultProps = {
  isFlush: false
};
var StyledWrapper = styled('div')(function (props) {
  return {
    fontSize: spacing(2.5),
    marginBottom: spacing(1),
    alignItems: 'center',
    display: props.isStacked ? 'flex' : 'inline-flex',
    flexDirection: props.isStacked && 'column',
    marginRight: !props.isStacked && spacing(2),
    color: props.theme.colours.bodyText
  };
});
var StyledSymbol = styled('div')(function (props) {
  return {
    backgroundColor: props.backgroundColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: spacing(0.5),
    color: colours.neutral.white,
    '-webkit-font-smoothing': 'antialiased',
    fontSize: '33px',
    paddingTop: spacing(0.5),
    marginBottom: spacing(1.5)
  };
}, function (props) {
  return !props.isStacked && {
    marginRight: spacing(2),
    marginBottom: 0
  };
});

var generateBg = function generateBg(hue) {
  return 'hsl(' + hue + ', 50%, 50%);';
};

var getHue = function getHue(name) {
  var str = hashString(name);
  var color = integerToHue(str);
  return color;
};

var DistinctiveGeneratedSymbol = function DistinctiveGeneratedSymbol(_ref) {
  var label = _ref.label,
      isStacked = _ref.isStacked,
      isSymbolOnly = _ref.isSymbolOnly,
      symbolColorOverride = _ref.symbolColorOverride;
  var hue = getHue(label);
  var initial = label.slice(0, 1);
  var bg = symbolColorOverride || generateBg(hue);
  return React.createElement(StyledWrapper, {
    isStacked: isStacked
  }, React.createElement(StyledSymbol, {
    backgroundColor: bg
  }, initial), !isSymbolOnly && React.createElement('div', null, React.createElement(ProminentLabel, {
    isFlush: true
  }, React.createElement('span', null, label))));
};

DistinctiveGeneratedSymbol.propTypes = {
  /** string label */
  label: PropTypes.string.isRequired,

  /** stacked layout boolean */
  isStacked: PropTypes.bool,

  /** Symbol only boolean */
  isSymbolOnly: PropTypes.bool,

  /** String applied to background css property */
  symbolColorOverride: PropTypes.string
};

var _templateObject$1 = taggedTemplateLiteral(['\n\tdisplay: flex;\n\tflex-direction: column;\n\tflex-grow: 1;\n'], ['\n\tdisplay: flex;\n\tflex-direction: column;\n\tflex-grow: 1;\n']),
    _templateObject2 = taggedTemplateLiteral(['\n\tdisplay: inline-flex;\n\tmargin-top: ', ';\n\tsvg {\n\t\theight: ', ';\n\t\twidth: ', ';\n\t}\n'], ['\n\tdisplay: inline-flex;\n\tmargin-top: ', ';\n\tsvg {\n\t\theight: ', ';\n\t\twidth: ', ';\n\t}\n']),
    _templateObject3 = taggedTemplateLiteral(['\n\tbackground: ', ';\n\tborder: 1px solid ', ';\n\tborder-left: 0;\n\tborder-radius: ', ';\n\tbox-shadow: ', ';\n\tcolor: ', ';\n\tdisplay: flex;\n\tmargin-bottom: ', ';\n\tpadding: ', ' ', ' ', ' ', ';\n\tposition: relative;\n\ttext-align: left;\n\twidth: 100%;\n\t:hover {\n\t\tcursor: ', ';\n\t}\n\t:before {\n\t\tbackground-color: ', ';\n\t\tcontent: \'\';\n\t\tdisplay: block;\n\t\theight: calc(100%);\n\t\tleft: 0;\n\t\tposition: absolute;\n\t\ttop: 0;\n\t\twidth: ', ';\n\t\tborder-radius: ', ' 0 0 ', ';\n\t}\n\t:focus {\n\t\toutline: ', ';\n\t\tbox-shadow: ', ';\n\t}\n'], ['\n\tbackground: ', ';\n\tborder: 1px solid ', ';\n\tborder-left: 0;\n\tborder-radius: ', ';\n\tbox-shadow: ', ';\n\tcolor: ', ';\n\tdisplay: flex;\n\tmargin-bottom: ', ';\n\tpadding: ', ' ', ' ', ' ', ';\n\tposition: relative;\n\ttext-align: left;\n\twidth: 100%;\n\t:hover {\n\t\tcursor: ', ';\n\t}\n\t:before {\n\t\tbackground-color: ', ';\n\t\tcontent: \'\';\n\t\tdisplay: block;\n\t\theight: calc(100%);\n\t\tleft: 0;\n\t\tposition: absolute;\n\t\ttop: 0;\n\t\twidth: ', ';\n\t\tborder-radius: ', ' 0 0 ', ';\n\t}\n\t:focus {\n\t\toutline: ', ';\n\t\tbox-shadow: ', ';\n\t}\n']);

var disableColor = colours.disabled.background;

var getBackgroundColor = function getBackgroundColor(props) {
  return !props.isDisabled ? 'hsl(' + props.hue + ', 50%, 50%)' : disableColor;
};

var StyledContentWrapper = styled('div')(_templateObject$1);
var StyledIconWrapper = styled('span')(_templateObject2, spacing(2), spacing(3), spacing(3));
var StyledWrapper$1 = styled('button')(_templateObject3, colours.backgrounds.component, colours.component.border, function (props) {
  return props.theme.borderRadius;
}, function (props) {
  return props.isDisabled ? '0' : function (props) {
    return props.theme.boxShadow;
  };
}, function (props) {
  return props.theme.colours.bodyText;
}, spacing(1), spacing(2), spacing(2), spacing(2), spacing(3), function (props) {
  return !props.isDisabled ? 'pointer' : 'auto';
}, getBackgroundColor, spacing(1), function (props) {
  return props.theme.borderRadius;
}, function (props) {
  return props.theme.borderRadius;
}, function (props) {
  return props.isDisabled && 'none';
}, function (props) {
  return props.isDisabled && 'none';
}); // eslint-disable-next-line react/prop-types

var DistinctiveLinkCard = function DistinctiveLinkCard(_ref) {
  var children = _ref.children,
      isDisabled = _ref.isDisabled,
      label = _ref.label,
      onClick = _ref.onClick,
      theme = _ref.theme;
  var hash = hashString(label);
  var hue = integerToHue(hash);

  var handleClick = function handleClick() {
    onClick();
  };

  var activeGenIcon = React.createElement(DistinctiveGeneratedSymbol, {
    label: label
  });
  var disabledGenIcon = React.createElement(DistinctiveGeneratedSymbol, {
    label: label,
    symbolColorOverride: disableColor
  });
  var genIcon = !isDisabled ? activeGenIcon : disabledGenIcon;
  var ChevronRightSolidLargeIcon = getChevronRightSolidLarge(theme.name);
  var chevron = !isDisabled && React.createElement(ChevronRightSolidLargeIcon, null);
  return React.createElement(StyledWrapper$1, {
    hue: hue,
    isDisabled: isDisabled,
    type: 'button',
    onClick: !isDisabled && handleClick
  }, React.createElement(StyledContentWrapper, null, genIcon, React.createElement('div', null, children)), React.createElement(StyledIconWrapper, null, chevron));
};

DistinctiveLinkCard.propTypes = {
  /** Child nodes */
  children: PropTypes.node,

  /** Boolean controls card style and onClick */
  isDisabled: PropTypes.bool,

  /** Card title String */
  label: PropTypes.string.isRequired,

  /** onClick callback function */
  onClick: PropTypes.func
};
DistinctiveLinkCard.defaultProps = {};
var WrappedDistinctiveLinkCard = withTheme(DistinctiveLinkCard);
export { Card, WrappedDistinctiveLinkCard as DistinctiveLinkCard };